import type {Config, Driver} from 'driver.js';
import type {Container} from '../../container.ts';
import type {OnboardingConfig, OnboardingProgress} from './types.ts';
import {moveToStep, completeOnboarding, removeURLParameter, getInstanceBaseUrl} from './utils.ts';
const {csrfToken} = window.config;

/**
 * Active the menu by adding specified CSS classes from an element.
 *
 * It is used prior to highlighting the element
 * to prevent the menu from being hidden when the element is highlighted.
 *
 * @param {Element} selector - The DOM element to remove CSS classes from.
 * @param {string[]} classArray - An array of CSS class names to remove from the element.
 */
function activateMenu(selector: Element, classArray: string[]) {
  // show menu prior when highlighting the element
  // to avoid the menu being hidden when the element is highlighted
  for (const entry of classArray) {
    selector.classList.add(entry);
  }
}

/**
 * Reset the menu by removing specified CSS classes from an element.
 *
 * @param {Element} selector - The DOM element to remove CSS classes from.
 * @param {string[]} classArray - An array of CSS class names to remove from the element.
 */
function resetMenu(selector: Element, classArray: string[]) {
  // show menu prior when highlighting the element
  // to avoid the menu being hidden when the element is highlighted
  for (const entry of classArray) {
    selector.classList.remove(entry);
  }
}

/**
 * Generates the configuration object for the onboarding wizard specific to the
 * dashboard using Driver.js.
 *
 * @param {Driver} driverObj - The Driver.js instance for creating the wizard.
 * @param {Function} introConfig - The introductory onboarding wizard config.
 * @param {Object} onboardingProgress - An object representing the user's onboarding progress.
 * @param {Container} container - The dependency injection providing access to necessary services.
 * @returns {Object} The configuration object for the dashboard-specific onboarding.
 */
export function dashboardConfig(driverObj: Driver, introConfig: (d: Driver, p: OnboardingProgress, c: Container) => OnboardingConfig, onboardingProgress: OnboardingProgress, container: Container): OnboardingConfig {
  const appSubUrl = container?.lookup('appSubUrl');

  return {
    showProgress: true,
    smoothScroll: true,
    allowClose: false,
    // Name should match a category within OnboardingSectionName
    name: 'Dashboard',

    onDestroyed: () => {
      const menu = document.querySelector(
        '[data-onboarding="create-icon"] .menu',
      );
      const classArray = ['gt-opacity-0', 'visible', 'left'];
      resetMenu(menu, classArray);
    },
    onPopoverRender: (popover) => {
      // Add a skip button to the popover footer
      const skipBtn = document.createElement('button');
      skipBtn.type = 'button';
      skipBtn.textContent = 'Exit Tour';
      popover.footerButtons.insertBefore(
        skipBtn,
        popover.footerButtons.firstChild,
      );

      skipBtn.addEventListener('click', () => {
        const createMenu = document.querySelector(
          '[data-onboarding="create-icon"] .menu',
        );
        const userMenu = document.querySelector(
          '[data-onboarding="user-menu"]',
        );
        const classArray = ['gt-opacity-0', 'visible', 'left'];
        resetMenu(createMenu, classArray);
        resetMenu(userMenu, classArray);

        const currentStepIndex = driverObj.getActiveIndex();
        const tourName = (driverObj.getConfig() as OnboardingConfig).name;

        // update the onboarding progress
        completeOnboarding(tourName, csrfToken, currentStepIndex, true, container);

        // remove the onboarding url param to prevent the tour
        // from starting again on page refresh
        removeURLParameter('onboarding');

        // update the config to the intro config
        driverObj.setConfig(introConfig(driverObj, onboardingProgress, container) as Config);
        // start the driver again
        driverObj.drive();
      });
    },
    steps: [
      {
        popover: {
          title: 'Dashboard Overview',
          description: `Your dashboard will show you all of the projects you're collaborating on and an activity feed for any projects you're watching.`,
          prevBtnText: '← Back to Onboarding',
          disableButtons: [],
          onPrevClick() {
            // update the config to the intro config
            driverObj.setConfig(introConfig(driverObj, onboardingProgress, container) as Config);
            // start the driver again
            driverObj.drive();
          },
        },
        onHighlightStarted: (_element, _step, opt) => {
          opt.config.disableActiveInteraction = false;
        },
      },
      {
        element: '[data-onboarding="logo"]',
        popover: {
          title: 'Left Navigation',
          description:
            'You can always navigate to the dashboard by selecting the electro-leaf icon.',
          onNextClick: () => {
            const menu = document.querySelector('#navbar-expand-toggle');
            const isMobile = window.innerWidth < 768;
            if (!menu.classList.contains('active') && isMobile) {
              (menu as HTMLElement).click();
            }
            driverObj.moveNext();
          },
        },
        onHighlightStarted: (_element, _step, opt) => {
          opt.config.disableActiveInteraction = true;
        },
      },
      {
        element: '[data-onboarding="help-icon"]',
        popover: {
          description: 'You can find additional help documentation here.',
        },
      },
      {
        element: '[data-onboarding="user-icon"]',
        popover: {
          title: 'User Menu',
          align: 'end',
          description: `<p>You can find and modify your user settings in the User Menu.</p>
            <p>Click the User Menu dropdown on the right to continue.</p>`,
          nextBtnText: 'Click User Menu',
          disableButtons: ['next'],
        },
        onHighlightStarted: (el, _step, opt) => {
          opt.config.disableActiveInteraction = false;
          const menu = document.querySelector('[data-onboarding="user-menu"]');
          // show menu prior when highlighting the element
          // to avoid the menu being hidden when the element is highlighted
          const classArray = [
            'gt-opacity-0',
            'transition',
            'visible',
            'left',
          ];
          activateMenu(menu, classArray);

          const currentStep = driverObj.getState().activeIndex + 1;
          moveToStep(currentStep, el, driverObj);
        },
      },
      {
        element: '[data-onboarding="user-menu"]',
        popover: {
          title: 'User Menu Items',
          description: 'You can find many useful options within the user menu.',
        },
        onHighlightStarted: (_element, _step, opt) => {
          opt.config.disableActiveInteraction = true;
          // remove the opacity class to show the menu
          const menu = document.querySelector(
            '[data-onboarding="user-icon"] .menu',
          );
          menu.classList.remove('gt-opacity-0');
        },
      },
      {
        element: '[data-onboarding="settings-icon"]',
        popover: {
          description: `User settings can be configured here. We'll come back to that later.`,
        },
        onHighlightStarted: (_element, _step, opt) => {
          opt.config.disableActiveInteraction = true;
          // show menu prior when highlighting the element
          // to avoid the menu being hidden when the element is highlighted
          const menu = document.querySelector(
            '[data-onboarding="user-icon"] .menu',
          );
          const classArray = ['transition', 'visible', 'left'];
          activateMenu(menu, classArray);
        },
      },
      {
        element: '[data-onboarding="create-icon"]',
        popover: {
          title: 'New Repo / Migrate Menu',
          description: `<p>It's lonely here, let's migrate an AllSpice demo repo to get things going.</p><p>Click the Dropdown Menu to continue.</p>`,
          nextBtnText: 'Click Dropdown Menu',
          disableButtons: ['next'],
          onPrevClick: () => {
            const menu = document.querySelector(
              '[data-onboarding="create-icon"] .menu',
            );
            // show menu prior when highlighting the element
            // to avoid the menu being hidden when the element is highlighted
            const classArray = ['transition', 'visible', 'left'];
            activateMenu(menu, classArray);
            driverObj.movePrevious();
          },
        },
        onHighlightStarted: (el, _step, opt) => {
          opt.config.disableActiveInteraction = false;

          const currentStep = driverObj.getState().activeIndex + 1;
          moveToStep(currentStep, el, driverObj);

          // hide previous menu prior when highlighting the element
          const userMenu = document.querySelector(
            '[data-onboarding="user-icon"] .menu',
          );
          const userMenuClassArray = ['visible', 'gt-opacity-0'];
          resetMenu(userMenu, userMenuClassArray);

          const menu = document.querySelector(
            '[data-onboarding="create-icon"] .menu',
          );
          // show menu prior when highlighting the element
          // to avoid the menu being hidden when the element is highlighted
          const classArray = ['gt-opacity-0', 'transition', 'visible', 'left'];
          activateMenu(menu, classArray);
        },
      },
      {
        element: '[data-onboarding="migrate-icon"]',
        popover: {
          title: 'Migrate Repo',
          description: `To start the process of migrating a demo repository to your user account, press "New Migration" to continue.`,
          showButtons: ['previous', 'next'],
          nextBtnText: 'Click New Migration',
          disableButtons: ['next'],
          onNextClick: () => {
            if (window.location.pathname === '/') {
              window.location.href = `${getInstanceBaseUrl(appSubUrl)}/repo/migrate?onboarding=migration-cards`;
            }
          },
        },
        onHighlightStarted: (el) => {
          // remove the opacity class to show the menu
          const menu = document.querySelector(
            '[data-onboarding="create-icon"] .menu',
          );
          resetMenu(menu, ['gt-opacity-0']);

          el.addEventListener('click', (e: Event) => {
            e.preventDefault();
            // navigate to url repo-migrate with url param onboarding=migration-cards
            // to trigger the correct onboarding step
            window.location.href = `${getInstanceBaseUrl(appSubUrl)}/repo/migrate?onboarding=migration-cards`;
          });
        },
      },
      {
        element: '[data-onboarding="allspice-migrate-card"]',
        id: 'migrate-card',
        popover: {
          description: `<p>We'll migrate an existing demo design repository in AllSpice Hub.</p><p>Click the AllSpice Migrator to continue.</p>`,
          showButtons: ['next'],
          disableButtons: ['next'],
          nextBtnText: 'Click AllSpice Migrator',
          onPrevClick: (): void => {
            return null;
          },
          onNextClick: (): void => {
            // navigate to url repo-migrate with url param onboarding=migration-form
            // to trigger the correct onboarding step
            window.location.href = `${getInstanceBaseUrl(appSubUrl)}/repo/migrate?service_type=3&org=&mirror=&onboarding=migration-form`;
          },
        },
        onHighlightStarted: (el) => {
          // remove the opacity class to show the menu
          const menu = document.querySelector(
            '[data-onboarding="create-icon"] .menu',
          );
          menu.classList.remove('gt-opacity-0');

          if (!el) return;
          el.addEventListener('click', (e: Event) => {
            e.preventDefault();
            // navigate to url repo-migrate with url param onboarding=migration-form
            // to trigger the correct onboarding step
            window.location.href = `${getInstanceBaseUrl(appSubUrl)}/repo/migrate?service_type=3&org=&mirror=&onboarding=migration-form`;
          });
        },
      },
      {
        element: '[data-onboarding="migrate-url-input"]',
        id: 'migrate-url-input',
        popover: {
          title: `Migration URL`,
          description: `This is the URL for the repository you want to migrate. We'll fill it for you this time.`,
          onPrevClick: () => {
            window.location.href = `${getInstanceBaseUrl(appSubUrl)}/repo/migrate?onboarding=migration-cards`;
          },
        },
        onHighlighted: (el) => {
          // auto-fill the input field with the demo repo URL
          if (!el) return;
          const input = el.querySelector('input');
          const event = new Event('input');
          input.value = 'https://hub.allspice.io/AllSpice-demos/Altium-archimajor-3d-printer-driver-demo';
          input.dispatchEvent(event);
        },
      },
      {
        element: '[data-onboarding="migrate-options"]',
        popover: {
          description: `By adding an access token, you can migrate even more data, but we won't worry about that yet.`,
        },
      },
      {
        element: '[data-onboarding="migrate-owner"]',
        popover: {
          description: `Here is where you can select who will own the repository initially.
          Typically, you'll have two options:
          <ul>
            <li>Your user: where you will have to manually add any additional collaborators to share.</li>
            <li>Your organization: where the repository will be automatically shared using the organization's team permission settings.</li>
          </ul>
          <p>For this demo, we won't choose anyone else, and we'll migrate the demo to your user account</p>`,
        },
      },
      {
        element: '[data-onboarding="migrate-button"]',
        popover: {
          title: 'Finish Migration',
          description: 'Click this button to finish the migration process.',
          onNextClick: (): void => {
            return null;
          },
          showButtons: ['previous'],
        },
        onHighlightStarted: (el) => {
          // the done button is removed so we capture the click event here
          // and save the users onboarding progress
          // then end the onboarding wizard
          el.addEventListener(
            'click',
            () => {
              const currentIndex = driverObj.getState().activeIndex;
              completeOnboarding('Dashboard', csrfToken, currentIndex, false, container);
              driverObj.destroy();
            },
            {once: true},
          );
        },
      },
    ],
  };
}
